import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, CalendarClock, ChevronDown } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { BoletasPanel } from "@/routes/payroll/BoletasPanel"
import { PageHeader } from "@/components/PageHeader"
import { SelectorDePeriodo } from "@/components/SelectorDePeriodo"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { etiquetaRegimen } from "@/lib/regimenes"
import { cn } from "@/lib/utils"
import type { PayrollRegime } from "@/lib/types"

import { fetchPlanilla } from "@/routes/payroll/api"
import type { PersonaEnPlanilla } from "@/routes/payroll/api"

/**
 * LA PLANILLA DEL PERÍODO.
 *
 * Punto 10 de Cesar: «en contabilidad deberíamos tener […] Payroll». Eligió
 * empezar por la vista y dejar las boletas en PDF para después.
 *
 * ===========================================================================
 * CINCO COLUMNAS Y NO UN TOTAL
 * ===========================================================================
 *
 * Un «costo de planilla» de 57.980 esconde justo lo que hay que ver. Las cinco
 * cifras contestan cinco preguntas distintas y quien mira una planilla las
 * necesita todas:
 *
 *   Devengado     lo que se le reconoce a la persona
 *   Descuentos    lo que se le retiene — ONP/AFP, renta de 5ta
 *   Neto          lo que de verdad recibe
 *   Aportes       lo que la empresa paga ENCIMA — EsSalud, SCTR, provisiones
 *   Costo empresa devengado + aportes
 *
 * Y una sexta que es la que salva el runway: **DIFERIDO**, lo que se devenga
 * este mes y sale de caja en otro. Gratificación en julio y diciembre, CTS en
 * mayo y noviembre. Una planilla que los mete en el total del mes dice que la
 * empresa va a pagar en marzo algo que pagará en julio.
 *
 * ===========================================================================
 * DONDE LA NORMA NO ESTÁ VERIFICADA, LA PANTALLA LO GRITA
 * ===========================================================================
 *
 * Sólo Perú tiene el rulepack laboral completo. En las otras tres sociedades los
 * aportes y las retenciones salen en CERO — no porque no existan, sino porque el
 * motor se niega a inventar un número plausible.
 *
 * Eso NO se puede enseñar como una planilla normal. Una planilla mexicana con
 * cero descuentos y cero aportes se lee como correcta, y una planilla se FIRMA.
 * El aviso va arriba, en ámbar, antes que las cifras.
 */
export function PlanillaPage() {
  const { activeTenantId, activeEntityId } = useAuth()
  const hoy = new Date()
  const [periodo, setPeriodo] = React.useState(
    `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`,
  )
  const [anio, mes] = periodo.split("-").map(Number)

  const q = useQuery({
    queryKey: ["payroll", activeTenantId, activeEntityId, anio, mes],
    queryFn: () => fetchPlanilla(anio, mes, activeEntityId),
    enabled: Boolean(activeTenantId) && Boolean(activeEntityId),
  })

  const d = q.data

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Vista contable"
        title={t("Planilla")}
        description={t("Quién cobra cuánto, qué le cuesta a la empresa y qué parte de eso NO sale de caja este mes. Los aportes los calcula la norma de cada país, no esta pantalla.")}
      />

      <Toolbar>
        <ToolbarField label={t("Período")} htmlFor="planilla-periodo">
          <SelectorDePeriodo id="planilla-periodo" value={periodo} onChange={setPeriodo} />
        </ToolbarField>
        <ToolbarNote icon={CalendarClock} className="ml-auto">
          {d ? `${d.sociedad} · ${d.moneda}` : "—"}
        </ToolbarNote>
      </Toolbar>

      {/*
        EL AVISO VA ANTES QUE LAS CIFRAS. Ver la cabecera: una planilla con los
        aportes en cero se lee como correcta, y se firma.
      */}
      {(d?.advertencias.length ?? 0) > 0 ? (
        <div className="space-y-1.5">
          {d?.advertencias.map((a) => (
            <p
              key={a}
              role="alert"
              className="flex items-start gap-1.5 rounded-md border border-warning/45 bg-warning-soft px-2.5 py-2 text-2xs leading-5"
            >
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
              {a}
            </p>
          ))}
        </div>
      ) : null}

      <MetricRow className="lg:grid-cols-4">
        <MetricCard label={t("Neto a pagar")} value={d?.totales.neto ?? 0}
                    loading={q.isLoading} tono="destacado" />
        <MetricCard label={t("Devengado")} value={d?.totales.devengado ?? 0}
                    loading={q.isLoading} />
        <MetricCard label={t("Aportes y provisiones")} value={d?.totales.aportes ?? 0}
                    loading={q.isLoading} />
        <MetricCard label={t("Costo para la empresa")} value={d?.totales.costo ?? 0}
                    loading={q.isLoading} />
      </MetricRow>

      {/*
        EL DIFERIDO, SOLO Y EN SU PROPIA FILA. No cabe entre los cuatro de
        arriba: aquéllos son del mes y éste dice cuánto de lo del mes NO se paga
        este mes. Ponerlo al lado lo haría parecer un quinto total.
      */}
      {d && d.totales.diferido > 0 ? (
        <p className="flex flex-wrap items-baseline gap-x-2 text-2xs leading-5 text-muted-foreground">
          <CalendarClock className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="num-strong text-foreground">
            {formatAmount(d.totales.diferido)}
          </span>
          {t("se devengan este mes y salen de caja en otro — gratificación, CTS y vacaciones. Están dentro del costo y NO del neto a pagar.")}
        </p>
      ) : null}

      <WorkPanel
        title={`Planilla ${periodo}`}
        meta={
          <span className="text-2xs text-muted-foreground">
            {d ? `${d.personas.length} persona(s)` : "—"}
          </span>
        }
        footnote={t("Pulsa una fila para ver sus conceptos, uno por uno, con la fecha en que cada uno sale de caja. El cálculo sale del rulepack del país: es el mismo que asienta la planilla en el libro.")}
      >
        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={Boolean(d) && d?.personas.length === 0}
          emptyMessage={t("No hay nadie en la planilla de este mes.")}
          emptyHint={t("La gente se carga en Contabilidad › Maestros › Personas, o por archivo con la plantilla de personas.")}
          onRetry={() => void q.refetch()}
          rows={6}
          columns={[24, 12, 12, 12, 12, 12, 12]}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16rem]">{t("Persona")}</TableHead>
                  <TableHead className="w-40">{t("Régimen")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Devengado")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Descuentos")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Neto")}</TableHead>
                  <TableHead className="w-28 text-right">{t("Aportes")}</TableHead>
                  <TableHead className="w-32 text-right">{t("Costo empresa")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(d?.personas ?? []).map((p) => (
                  <Fila key={p.employee_id} p={p} pais={d?.pais ?? null} />
                ))}
                {d && d.personas.length > 0 ? (
                  <TableRow className="bg-surface-sunken font-semibold">
                    <TableCell>{t("Total")}</TableCell>
                    <TableCell />
                    <TableCell className="num">{formatAmount(d.totales.devengado)}</TableCell>
                    <TableCell className="num">{formatAmount(d.totales.descuentos)}</TableCell>
                    <TableCell className="num">{formatAmount(d.totales.neto)}</TableCell>
                    <TableCell className="num">{formatAmount(d.totales.aportes)}</TableCell>
                    <TableCell className="num">{formatAmount(d.totales.costo)}</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </DataState>
      </WorkPanel>

      {/*
        LAS BOLETAS VAN DESPUÉS DE LA PLANILLA y no en la barra de arriba.

        Se bajan cuando ya se revisó que los números están bien, que es lo que
        se acaba de hacer al recorrer la tabla. Un botón de descarga arriba
        invita a bajarlas antes de mirarlas — y una boleta mal es una boleta que
        alguien ya firmó.
      */}
      <BoletasPanel anio={anio} mes={mes} periodo={periodo} />
    </div>
  )
}

/**
 * Una persona, y sus conceptos al desplegarla.
 *
 * El detalle se pliega porque una planilla de cuarenta personas con siete
 * conceptos cada una son doscientas ochenta filas: lo que se mira primero es el
 * neto de cada uno, y el desglose sólo cuando algo no cuadra.
 */
function Fila({ p, pais }: { p: PersonaEnPlanilla; pais: string | null }) {
  const [abierta, setAbierta] = React.useState(false)

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-row-hover"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
      >
        <TableCell className="font-medium">
          <span className="flex items-center gap-1.5">
            <ChevronDown
              className={cn("h-3 w-3 shrink-0 text-muted-foreground transition-transform",
                            abierta && "rotate-180")}
              aria-hidden="true"
            />
            <span className="truncate">{p.nombres}</span>
            {p.documento ? (
              <span className="code shrink-0 text-2xs text-muted-foreground">
                {p.documento}
              </span>
            ) : null}
          </span>
        </TableCell>
        <TableCell className="text-2xs text-muted-foreground">
          {/* Con el nombre de SU país: la «4ta» es peruana. Ver `lib/regimenes`. */}
          {etiquetaRegimen(p.regimen as PayrollRegime, pais)}
        </TableCell>
        <TableCell className="num">{formatAmount(p.devengado)}</TableCell>
        <TableCell className="num text-muted-foreground">
          {p.descuentos ? formatAmount(p.descuentos) : ""}
        </TableCell>
        <TableCell className="num font-medium">{formatAmount(p.neto)}</TableCell>
        <TableCell className="num text-muted-foreground">
          {p.aportes ? formatAmount(p.aportes) : ""}
        </TableCell>
        <TableCell className="num">{formatAmount(p.costo_empresa)}</TableCell>
      </TableRow>

      {abierta ? (
        <TableRow className="animate-row-in">
          <TableCell colSpan={7} className="bg-surface-sunken/50 p-0">
            <ul className="divide-y">
              {p.conceptos.map((c) => (
                <li
                  key={c.codigo}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-1.5 text-2xs"
                >
                  <span className="code shrink-0 text-muted-foreground">{c.codigo}</span>
                  <span className="min-w-0 flex-1 truncate">{c.nombre}</span>
                  <Badge variant="outline" className="shrink-0">
                    {c.paga === "EMPLOYER" ? t("lo paga la empresa") : t("se le descuenta")}
                  </Badge>
                  {/*
                    LA FECHA DE CAJA, cuando no es la del mes. Es el dato que
                    hace que la proyección no mienta y no se ve en ningún otro
                    sitio de la aplicación.
                  */}
                  {c.diferido ? (
                    <Badge
                      variant="warning-soft"
                      className="shrink-0"
                      title={c.fecha_caja
                        ? `Sale de caja el ${c.fecha_caja}`
                        : "Se devenga ahora y se paga en otro mes"}
                    >
                      {c.fecha_caja ? `caja ${c.fecha_caja}` : "diferido"}
                    </Badge>
                  ) : null}
                  <span className="num w-28 shrink-0 text-right">
                    {formatAmount(c.importe)}
                  </span>
                </li>
              ))}
            </ul>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}
